import React from 'react';
import { Container, Paper, TertiaryButton, TabsPanel, TertiaryButtonGroup, Typography as T } from '@dev/ee-components';
import { convertToLocalTime, safelyDecodeBase64Url } from '@dev/ee-tools';
import styled from 'styled-components';
import { withTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { compose } from 'react-recompose';
import { withRouter } from 'react-router';

import { ViewEmailStart } from '@/store/emails/actions';
import { ShowDialog } from '@/store/dialog/actions';
import { ApplicationState as State } from '@/store';
import { ShowDialogParams } from '@/store/dialog/types';

import EmailHeaders from './EmailHeaders';
import { DispatchProps, Props, ViewEmailProps } from './ViewEmailProps';

const dispatchProps = {
    ViewEmailStart,
    ShowDialog,
};

const mapState = (state: State, ownProps: ViewEmailProps): ViewEmailProps => ({
    ...ownProps,
    account: state.account,
    router: state.router,
    email: state.emails.email,
});

const Wrapper = styled.div`
    & .headerLine {
        padding: 22px 0;
        display: flex;
        justify-content: space-between;
    }
    & .headerLine > div:first-of-type {
        font-weight: 600;
    }
    & .headerLine + .headerLine {
        border-top: 1px solid #edf2f7;
    }

    & iframe {
        margin-top: 16px;
        width: 100%;
        height: 520px;
        border: 0;
        border-radius: 4px;
    }
`;

const HeaderContainer = styled.div`
    margin-bottom: 32px;
`;

class ViewEmail extends React.Component<Props> {
    public componentDidMount(): void {
        this.props.ViewEmailStart(this.props.match.params.msgId);
    }

    private renderEmailHeaders(): JSX.Element {
        const { t, email, match } = this.props;
        return (
            <div>
                <div className="headerLine">
                    <T variant="body2">{t('from')}</T>
                    <T variant="caption">{email?.Status?.FromEmail}</T>
                </div>
                <div className="headerLine">
                    <T variant="body2">{t('to')}</T>
                    <T variant="caption">{email?.Status?.ToEmail}</T>
                </div>
                <div className="headerLine">
                    <T variant="body2">{t('subject')}</T>
                    <T variant="caption">{email?.Preview?.Subject}</T>
                </div>
                <div className="headerLine">
                    <T variant="body2">{t('messageid')}</T>
                    <T variant="caption">{match.params.msgId}</T>
                </div>
                <div className="headerLine">
                    <T variant="body2">{t('sent')}</T>
                    <T variant="caption">{convertToLocalTime(email?.Status?.SentDate, 'L')}</T>
                </div>
            </div>
        );
    }

    private renderEmailIframe(): JSX.Element {
        const { email } = this.props;
        return <iframe title={email?.Preview?.Subject} srcDoc={email?.Preview?.Body} />;
    }

    private showInNewWindow(): void {
        const EmailPreview = window.open('', 'EmailPreview');
        EmailPreview.document.write(this.props.email.Preview?.Body);
    }

    private showEmailHeaders(): void {
        const { t, email } = this.props;
        const headers = <EmailHeaders headers={email.Headers} />;

        const params: ShowDialogParams = {
            title: t('emailheaders'),
            cancelLabel: t('close'),
            body: headers,
        };

        this.props.ShowDialog(params);
    }

    public render(): JSX.Element {
        const { t, match, email } = this.props;
        const breadCrumbs = match.params.contact ? safelyDecodeBase64Url(match.params.contact) : t('back');

        return (
            <Container variant="basic">
                <Wrapper>
                    <HeaderContainer>
                        <TabsPanel
                            title={email?.Preview.Subject}
                            breadcrumbs={breadCrumbs}
                            breadcrumbsOnClick={(): void => {
                                history.back();
                            }}
                        />
                        <TertiaryButtonGroup>
                            <TertiaryButton
                                onClick={(): void => {
                                    this.showInNewWindow();
                                }}
                                text={t('viewinnewwindow')}
                            />
                            <TertiaryButton
                                onClick={(): void => {
                                    this.showEmailHeaders();
                                }}
                                text={t('emailheaders')}
                            />
                        </TertiaryButtonGroup>
                    </HeaderContainer>

                    <Paper padding={{ top: 32, right: 48, bottom: 32, left: 32 }}>
                        {this.renderEmailHeaders()}
                        {this.renderEmailIframe()}
                    </Paper>
                </Wrapper>
            </Container>
        );
    }
}

export default compose(
    withRouter,
    withTranslation(),
    connect<ViewEmailProps, DispatchProps>(mapState, dispatchProps)
)(ViewEmail);
